import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Skeleton,
  Chip,
} from "@mui/material";
import {
  ContentCopy,
  CheckCircle,
  Router,
  Hub,
  SettingsEthernet,
  Wifi,
  Shield,
  VerifiedUser,
  Key,
  FileDownload,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../auth/useAuth";

import { APP_API_BASE } from "../../../config/api";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  `${APP_API_BASE}`;

function useCopy() {
  const [copiedKey, setCopiedKey] = useState(null);

  const copy = (key, value) => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return { copiedKey, copy };
}

function Row({
  icon,
  label,
  value,
  fieldKey,
  copiedKey,
  onCopy,
  children,
  alwaysShowCopy = false,
}) {
  const isCopied = copiedKey === fieldKey;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: "14px 16px",
        borderRadius: "12px",
        border: "1.5px solid",
        borderColor: isCopied ? "#bbf7d0" : "#F1F5F9",
        background: isCopied
          ? "linear-gradient(135deg,#f0fdf4,#dcfce7)"
          : "#FAFBFF",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: "#BFDBFE",
          background: "#EFF6FF",
          "& .copy-btn": { opacity: 1 },
        },
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: "10px",
          background: isCopied
            ? "linear-gradient(135deg,#22c55e,#16a34a)"
            : "linear-gradient(135deg,#1976d2,#42a5f5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
        }}
      >
        {isCopied ? <CheckCircle sx={{ fontSize: 18 }} /> : icon}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            color: "#94A3B8",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>

        <Typography
          sx={{
            fontSize: "13px",
            fontFamily: "monospace",
            color: "#0F172A",
          }}
        >
          {value}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {children}
        <Tooltip title="Copy">
          <IconButton
            className="copy-btn"
            size="small"
            onClick={() => onCopy(fieldKey, value)}
            sx={{
              opacity: alwaysShowCopy ? 1 : 0,
              transition: "0.2s",
              background: "#EFF6FF",
              border: "1px solid #BFDBFE",
            }}
          >
            <ContentCopy sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function MqttConnectionCard({
  deviceId,
  credentialRefreshKey = 0,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, msg: "" });

  const { activeOrg } = useAuth();
  const { copiedKey, copy } = useCopy();

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;

    setLoading(true);

    axios
      .get(`${API_BASE}/api/v1/devices/${deviceId}/connection`, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      })
      .then((res) => setData(res.data))
      .finally(() => setLoading(false));
  }, [deviceId, activeOrg?.id, credentialRefreshKey]);

  const handleCopy = (key, value) => {
    copy(key, String(value ?? ""));
    setSnackbar({ open: true, msg: `${key} copied` });
  };

  const fetchTlsFileText = async (url) => {
    const token = localStorage.getItem("authToken");
    if (!token || !url) return null;

    const response = await axios.get(`${API_BASE}${url}`, {
      responseType: "blob",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
      },
    });

    return response.data.text();
  };

  const handleDownload = async (url, filename) => {
    const token = localStorage.getItem("authToken");
    if (!token || !url) return;

    try {
      const response = await axios.get(`${API_BASE}${url}`, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${token}`,
          ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
        },
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setSnackbar({ open: true, msg: `${filename} downloaded` });
    } catch (error) {
      setSnackbar({ open: true, msg: `Unable to download ${filename}` });
    }
  };

  const handleTlsCopy = async (key, fallbackValue, url, successLabel) => {
    if (!url) {
      handleCopy(key, fallbackValue);
      return;
    }

    try {
      const text = await fetchTlsFileText(url);
      if (!text) {
        throw new Error("empty TLS file");
      }
      copy(key, text);
      setSnackbar({ open: true, msg: `${successLabel} copied to clipboard` });
    } catch (error) {
      setSnackbar({ open: true, msg: `Unable to copy ${successLabel}` });
    }
  };

  const isTls =
    String(data?.security_mode || "PLAINTEXT").toUpperCase() === "TLS";

  return (
    <>
      <Box
        sx={{
          borderRadius: "18px",
          overflow: "hidden",
          border: "1.5px solid #E2E8F0",
          background: "#fff",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "linear-gradient(135deg,#EFF6FF,#F0F9FF)",
            borderBottom: "1.5px solid #E2E8F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "12px",
                background: "linear-gradient(135deg,#1976d2,#42a5f5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
              }}
            >
              <Wifi />
            </Box>

            <Box>
              <Typography fontWeight={700} fontSize={15}>
                MQTT Connection
              </Typography>
              <Typography fontSize={11} color="#64748B">
                Broker connection details
              </Typography>
            </Box>
          </Box>

          <Chip
            label={isTls ? "TLS Secure" : "Password Auth"}
            size="small"
            sx={{
              background: isTls ? "#DCFCE7" : "#DBEAFE",
              color: isTls ? "#15803D" : "#1D4ED8",
              fontWeight: 600,
              fontSize: "11px",
            }}
          />
        </Box>

        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {loading ? (
            [1, 2, 3].map((i) => (
              <Box
                key={i}
                sx={{
                  p: "14px 16px",
                  borderRadius: "12px",
                  border: "1.5px solid #F1F5F9",
                }}
              >
                <Skeleton width={120} height={14} />
                <Skeleton width="70%" height={20} />
              </Box>
            ))
          ) : (
            <>
              <Row
                icon={<Router sx={{ fontSize: 18 }} />}
                label="Broker"
                value={data?.broker || "—"}
                fieldKey="broker"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />

              <Row
                icon={<SettingsEthernet sx={{ fontSize: 18 }} />}
                label="Protocol"
                value={`${(data?.protocol || "mqtt").toUpperCase()} v3.1.1`}
                fieldKey="protocol"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />

              <Row
                icon={<Hub sx={{ fontSize: 18 }} />}
                label="Port"
                value={`${data?.port || "—"}${isTls ? " (TLS)" : ""}`}
                fieldKey="port"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />

              <Row
                icon={<Wifi sx={{ fontSize: 18 }} />}
                label="Telemetry Topic"
                value={data?.telemetry_topic || "—"}
                fieldKey="telemetry"
                copiedKey={copiedKey}
                onCopy={handleCopy}
              />

              {isTls && (
                <>
                  <Row
                    icon={<Shield sx={{ fontSize: 18 }} />}
                    label="CA Certificate"
                    value={data?.ca_cert_filename || "ca.crt"}
                    fieldKey="ca_cert_filename"
                    copiedKey={copiedKey}
                    onCopy={() =>
                      handleTlsCopy(
                        "ca_cert_filename",
                        data?.ca_cert_filename || "ca.crt",
                        data?.ca_cert_download_url,
                        "CA certificate",
                      )
                    }
                    alwaysShowCopy
                    children={
                      data?.ca_cert_download_url ? (
                        <Tooltip title="Download CA certificate">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(
                                data.ca_cert_download_url,
                                data?.ca_cert_filename || "ca.crt",
                              )
                            }
                            sx={{
                              background: "#EFF6FF",
                              border: "1px solid #BFDBFE",
                              "&:hover": { background: "#DBEAFE" },
                            }}
                          >
                            <FileDownload sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                  <Row
                    icon={<VerifiedUser sx={{ fontSize: 18 }} />}
                    label="Device Certificate"
                    value={data?.client_cert_filename || "device.crt"}
                    fieldKey="client_cert_filename"
                    copiedKey={copiedKey}
                    onCopy={() =>
                      handleTlsCopy(
                        "client_cert_filename",
                        data?.client_cert_filename || "device.crt",
                        data?.client_cert_download_url,
                        "device certificate",
                      )
                    }
                    alwaysShowCopy
                    children={
                      data?.client_cert_download_url ? (
                        <Tooltip title="Download device certificate">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(
                                data.client_cert_download_url,
                                data?.client_cert_filename || "device.crt",
                              )
                            }
                            sx={{
                              background: "#EFF6FF",
                              border: "1px solid #BFDBFE",
                              "&:hover": { background: "#DBEAFE" },
                            }}
                          >
                            <FileDownload sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                  <Row
                    icon={<Key sx={{ fontSize: 18 }} />}
                    label="Device Key"
                    value={data?.client_key_filename || "device.key"}
                    fieldKey="client_key_filename"
                    copiedKey={copiedKey}
                    onCopy={() =>
                      handleTlsCopy(
                        "client_key_filename",
                        data?.client_key_filename || "device.key",
                        data?.client_key_download_url,
                        "device key",
                      )
                    }
                    alwaysShowCopy
                    children={
                      data?.client_key_download_url ? (
                        <Tooltip title="Download device key">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(
                                data.client_key_download_url,
                                data?.client_key_filename || "device.key",
                              )
                            }
                            sx={{
                              background: "#EFF6FF",
                              border: "1px solid #BFDBFE",
                              "&:hover": { background: "#DBEAFE" },
                            }}
                          >
                            <FileDownload sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                </>
              )}
            </>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
